import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { AuthContext } from '@/App';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/shared/Header';
import Footer from '@/components/shared/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import { 
  Download, 
  User, 
  Mail, 
  Phone, 
  MapPin,
  Calendar,
  Award,
  Briefcase,
  GraduationCap,
  Save,
  Eye
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CVBuilderPage = () => {
  const { user, isAuthenticated } = useContext(AuthContext);
  const navigate = useNavigate();
  const cvRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [cvData, setCvData] = useState({
    fullName: '',
    fatherName: '',
    email: '',
    phone: '',
    address: '',
    dob: '',
    objective: '',
    education: '',
    experience: '',
    skills: '',
    languages: 'Hindi, English'
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth/login');
      return;
    }
    fetchData();
  }, [isAuthenticated, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Get user's certificates
      const resultsRes = await axios.get(`${API}/assessment/my-results`);
      const passedResults = resultsRes.data.filter(r => r.result === 'pass');
      
      // Get course details for each certificate
      const coursesRes = await axios.get(`${API}/assessment/courses`);
      const enrichedCerts = passedResults.map(result => {
        const course = coursesRes.data.find(c => c.id === result.course_id);
        return {
          ...result,
          courseName: course?.name || result.course_name,
          duration: course?.duration || 'N/A'
        };
      });
      
      setCertificates(enrichedCerts);
      
      // Pre-fill user data
      setCvData(prev => ({
        ...prev,
        fullName: user?.name || '',
        fatherName: user?.father_name || '',
        email: user?.email || '',
        phone: user?.phone || '',
        dob: user?.dob || ''
      }));
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCvData(prev => ({ ...prev, [name]: value }));
  };

  const handleDownload = async () => {
    if (!cvRef.current) return;
    setDownloading(true);
    
    try {
      const canvas = await html2canvas(cvRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true
      });
      
      const link = document.createElement('a');
      link.download = `CV_${cvData.fullName.replace(/\s+/g, '_')}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast.success('CV downloaded successfully!');
    } catch (error) {
      toast.error('Failed to download CV');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin h-10 w-10 border-4 border-blue-600 border-t-transparent rounded-full"></div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">CV Builder</h1>
            <p className="text-slate-600">Create your professional CV with your certified skills</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowPreview(!showPreview)}>
              <Eye className="h-4 w-4 mr-2" /> {showPreview ? 'Edit Mode' : 'Preview'}
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleDownload} disabled={downloading}>
              <Download className="h-4 w-4 mr-2" /> {downloading ? 'Downloading...' : 'Download CV'}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Form Section */}
          {!showPreview && (
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Personal Information</h2>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="fullName">Full Name</Label>
                    <Input 
                      id="fullName" 
                      name="fullName" 
                      value={cvData.fullName} 
                      onChange={handleChange}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="fatherName">Father's Name</Label>
                    <Input 
                      id="fatherName" 
                      name="fatherName" 
                      value={cvData.fatherName} 
                      onChange={handleChange}
                      className="mt-1"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input 
                      id="email" 
                      name="email" 
                      type="email"
                      value={cvData.email} 
                      onChange={handleChange}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Input 
                      id="phone" 
                      name="phone" 
                      value={cvData.phone} 
                      onChange={handleChange}
                      className="mt-1"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="dob">Date of Birth</Label>
                    <Input 
                      id="dob" 
                      name="dob" 
                      type="date"
                      value={cvData.dob} 
                      onChange={handleChange}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="languages">Languages</Label>
                    <Input 
                      id="languages" 
                      name="languages" 
                      value={cvData.languages} 
                      onChange={handleChange}
                      className="mt-1"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="address">Address</Label>
                  <Input 
                    id="address" 
                    name="address" 
                    value={cvData.address} 
                    onChange={handleChange}
                    placeholder="City, State"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label htmlFor="objective">Career Objective</Label>
                  <Textarea 
                    id="objective" 
                    name="objective" 
                    value={cvData.objective} 
                    onChange={handleChange}
                    rows={3}
                    placeholder="A brief statement about your career goals..."
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label htmlFor="education">Education</Label>
                  <Textarea 
                    id="education" 
                    name="education" 
                    value={cvData.education} 
                    onChange={handleChange}
                    rows={3}
                    placeholder="10th - Board Name, Year&#10;12th - Board Name, Year&#10;Graduation - University, Year"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label htmlFor="experience">Work Experience (if any)</Label>
                  <Textarea 
                    id="experience" 
                    name="experience" 
                    value={cvData.experience} 
                    onChange={handleChange}
                    rows={3}
                    placeholder="Job Title - Company Name, Duration&#10;Key responsibilities..."
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label htmlFor="skills">Additional Skills</Label>
                  <Textarea 
                    id="skills" 
                    name="skills" 
                    value={cvData.skills} 
                    onChange={handleChange}
                    rows={2}
                    placeholder="MS Office, Communication, Team Work..."
                    className="mt-1"
                  />
                </div>
              </div>
              
              {/* Certificates Info */}
              <div className="mt-6 p-4 bg-emerald-50 rounded-lg">
                <h3 className="font-semibold text-emerald-800 mb-2">
                  <Award className="h-4 w-4 inline mr-2" />
                  Your Certified Skills ({certificates.length})
                </h3>
                {certificates.length > 0 ? (
                  <ul className="text-sm text-emerald-700 space-y-1">
                    {certificates.map((cert, idx) => (
                      <li key={idx}>• {cert.courseName} ({cert.duration})</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-emerald-600">No certificates yet. Pass an exam to add skills!</p>
                )}
              </div>
            </div>
          )}

          {/* CV Preview */}
          <div className={showPreview ? 'col-span-2' : ''}>
            <div 
              ref={cvRef}
              className="bg-white rounded-xl shadow-lg mx-auto"
              style={{ width: '794px', minHeight: '1123px', padding: '40px' }}
            >
              {/* Header */}
              <div className="border-b-4 border-blue-600 pb-4 mb-6">
                <h1 className="text-3xl font-bold text-slate-900">{cvData.fullName || 'Your Name'}</h1>
                <p className="text-lg text-slate-600 mt-1">S/o {cvData.fatherName || 'Father Name'}</p>
                
                <div className="flex flex-wrap gap-4 mt-3 text-sm text-slate-600">
                  {cvData.email && (
                    <span className="flex items-center gap-1">
                      <Mail className="h-4 w-4 text-blue-600" /> {cvData.email}
                    </span>
                  )}
                  {cvData.phone && (
                    <span className="flex items-center gap-1">
                      <Phone className="h-4 w-4 text-blue-600" /> {cvData.phone}
                    </span>
                  )}
                  {cvData.address && (
                    <span className="flex items-center gap-1">
                      <MapPin className="h-4 w-4 text-blue-600" /> {cvData.address}
                    </span>
                  )}
                  {cvData.dob && (
                    <span className="flex items-center gap-1">
                      <Calendar className="h-4 w-4 text-blue-600" /> DOB: {cvData.dob}
                    </span>
                  )}
                </div>
              </div>

              {/* Career Objective */}
              {cvData.objective && (
                <div className="mb-6">
                  <h2 className="text-lg font-bold text-blue-600 border-b border-blue-200 pb-1 mb-2">
                    CAREER OBJECTIVE
                  </h2>
                  <p className="text-sm text-slate-700 whitespace-pre-line">{cvData.objective}</p>
                </div>
              )}

              {/* Certified Skills - Most Important */}
              <div className="mb-6">
                <h2 className="text-lg font-bold text-blue-600 border-b border-blue-200 pb-1 mb-2">
                  <Award className="h-4 w-4 inline mr-2" />
                  CERTIFIED SKILLS (SKILL INDIA)
                </h2>
                {certificates.length > 0 ? (
                  <div className="space-y-2">
                    {certificates.map((cert, idx) => (
                      <div key={idx} className="flex justify-between items-center bg-emerald-50 p-2 rounded">
                        <div>
                          <p className="font-semibold text-slate-900">{cert.courseName}</p>
                          <p className="text-xs text-slate-600">
                            Duration: {cert.duration} | Grade: {cert.percentage >= 80 ? 'A' : cert.percentage >= 60 ? 'B' : 'C'} ({cert.percentage?.toFixed(0)}%)
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-emerald-600 font-semibold">IQRA SKILLS Certified</p>
                          <p className="text-xs text-slate-500">Skill India Partner</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 italic">No certified skills yet</p>
                )}
              </div>

              {/* Education */}
              {cvData.education && (
                <div className="mb-6">
                  <h2 className="text-lg font-bold text-blue-600 border-b border-blue-200 pb-1 mb-2">
                    <GraduationCap className="h-4 w-4 inline mr-2" />
                    EDUCATION
                  </h2>
                  <p className="text-sm text-slate-700 whitespace-pre-line">{cvData.education}</p>
                </div>
              )}

              {/* Work Experience */}
              {cvData.experience && (
                <div className="mb-6">
                  <h2 className="text-lg font-bold text-blue-600 border-b border-blue-200 pb-1 mb-2">
                    <Briefcase className="h-4 w-4 inline mr-2" />
                    WORK EXPERIENCE
                  </h2>
                  <p className="text-sm text-slate-700 whitespace-pre-line">{cvData.experience}</p>
                </div>
              )}

              {/* Additional Skills */}
              {cvData.skills && (
                <div className="mb-6">
                  <h2 className="text-lg font-bold text-blue-600 border-b border-blue-200 pb-1 mb-2">
                    ADDITIONAL SKILLS
                  </h2>
                  <p className="text-sm text-slate-700">{cvData.skills}</p>
                </div>
              )}

              {/* Languages */}
              {cvData.languages && (
                <div className="mb-6">
                  <h2 className="text-lg font-bold text-blue-600 border-b border-blue-200 pb-1 mb-2">
                    LANGUAGES
                  </h2>
                  <p className="text-sm text-slate-700">{cvData.languages}</p>
                </div>
              )}

              {/* Footer */}
              <div className="mt-auto pt-4 border-t border-slate-200 text-center text-xs text-slate-500">
                <p>Generated by IQRA SKILLS PVT LTD | Partner: Skill India</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CVBuilderPage;
